import React from 'react'
import {
  NarrativeBlock as NarrativeBlockData,
  NarrativeSectionKey,
} from '@marketing-milk/interfaces'
import { ContentEditableInput } from 'components/mentions/ContentEditableInput'
import { MentionSuggestions } from 'components/mentions/MentionSuggestions'
import { SurfacePalette, type, radius } from '../../tokens'
import { useNarrativeBlockSave } from './useNarrativeBlockSave'

interface RowProps {
  palette: SurfacePalette
  packetID: number
  narrative: NarrativeBlockData[]
  sectionKey: NarrativeSectionKey
  label: string
  placeholder: string
}

const BudgetRow = ({ palette, packetID, narrative, sectionKey, label, placeholder }: RowProps) => {
  const {
    text,
    setText,
    setCursorPos,
    controlledCursorPos,
    setControlledCursorPos,
    mentionState,
    indicator,
    wrapperRef,
  } = useNarrativeBlockSave(packetID, sectionKey, narrative)

  const {
    isShowingSuggestions,
    filteredUsers,
    selectedIndex,
    selectUser,
    handleKeyDown,
    taggedUsers,
  } = mentionState

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        style={{
          fontFamily: type.family.mono,
          fontSize: type.size.eyebrow,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: palette.textMuted,
        }}
      >
        {label}
      </div>
      <div
        ref={wrapperRef}
        style={{
          position: 'relative',
          border: `1px solid ${palette.hairline}`,
          borderRadius: radius.sm,
          padding: '8px 10px',
          fontFamily: type.family.sans,
          fontSize: type.size.body,
          color: palette.text,
        }}
      >
        {isShowingSuggestions && (
          <MentionSuggestions
            users={filteredUsers}
            selectedIndex={selectedIndex}
            onSelect={u => {
              const result = selectUser(u)
              if (result) {
                setText(result.newValue)
                setControlledCursorPos(result.newCursorPosition)
              }
            }}
          />
        )}
        <ContentEditableInput
          value={text}
          onChange={setText}
          onCursorPositionChange={setCursorPos}
          cursorPosition={controlledCursorPos}
          onKeyDown={e => {
            const result = handleKeyDown(e)
            if (result && typeof result === 'object') {
              setText(result.newValue)
              setControlledCursorPos(result.newCursorPosition)
            }
          }}
          placeholder={placeholder}
          taggedUsers={taggedUsers}
        />
      </div>
      <div
        style={{
          fontFamily: type.family.mono,
          fontSize: 11,
          color: palette.textFaint,
          minHeight: 14,
        }}
      >
        {indicator}
      </div>
    </div>
  )
}

interface Props {
  palette: SurfacePalette
  packetID: number
  narrative: NarrativeBlockData[]
}

const ROWS: Array<{ sectionKey: NarrativeSectionKey; label: string; placeholder: string }> = [
  {
    sectionKey: 'budget-current',
    label: 'Current monthly budget',
    placeholder: 'e.g. $1,500 / month',
  },
  {
    sectionKey: 'budget-target',
    label: 'Target-ROI budget',
    placeholder: 'e.g. $2,200 / month to hit target ROI',
  },
  {
    sectionKey: 'budget-increase',
    label: 'Suggested monthly increase',
    placeholder: 'e.g. step up to $1,800 next sprint, then $2,200',
  },
  {
    sectionKey: 'budget-projected',
    label: 'Projected results',
    placeholder: 'e.g. ~15 more conversions at target CPA',
  },
]

export const BudgetRecommendationBlock = ({ palette, packetID, narrative }: Props) => (
  <div
    style={{
      marginTop: 16,
      padding: 16,
      border: `1px solid ${palette.hairline}`,
      borderRadius: radius.md,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}
  >
    <div
      style={{
        fontFamily: type.family.sans,
        fontSize: type.size.h3,
        fontWeight: type.weight.semibold,
        color: palette.text,
      }}
    >
      Budget Recommendation
    </div>
    {ROWS.map(r => (
      <BudgetRow
        key={r.sectionKey}
        palette={palette}
        packetID={packetID}
        narrative={narrative}
        sectionKey={r.sectionKey}
        label={r.label}
        placeholder={r.placeholder}
      />
    ))}
  </div>
)
